import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../../utils/axios";
import EntityManagement from "../../components/formTemplates/EntityManagement";
import EntityTable from "../../components/templateComponents/EntityTable";
import useDropdownFetch from "../../components/apiFetch/useDropdownFetch";

const Building = () => {
  const [formData, setFormData] = useState({
    bldg_code: "",
    bldg_no: "",
    bldg_name: "",
    est_id: "",
    campusid: "",
    area_id: "",
    csc_id: "",
    status_id: "",
    added_date: "",
  });

  const [entities, setEntities] = useState([]);
  const [dropdownData, setDropdownData] = useState({
    est_id: [],
    campusid: [],
    area_id: [],
    csc_id: [],
    status_id: [],
  });

  const [editMode, setEditMode] = useState(false);
  const [editingEntityId, setEditingEntityId] = useState(null);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [selectedEstId, setSelectedEstId] = useState("");
  const [selectedCampusId, setSelectedCampusId] = useState("");

  const [qrCodes, setQrCodes] = useState({});

  const firstFormFieldRef = useRef(null);

  const endpoints = {
    establishment: "/view_establishment/v1.1",
    campus: "/view_campus/v1.1",
    status: "/view_status/v0.1",
    buildings: "/view_building/v1.1",
    updateBuilding: "/update_building/v1.1",
    registerBuilding: "/registration_building/v1.1",
    csc: "/city/v0.1",
    area: "/area/v0.1",
  };

  useEffect(() => {
    const fetchStaticDropdowns = async () => {
      try {
      
        const estRes = await axiosInstance.post(endpoints.establishment, {
          area_id: 1,
          view_type: "view",
        });
        const estOptions = Object.entries(estRes.data.Data || {}).map(([value, label]) => ({
          value,
          label,
        }));

       
        const cscRes = await axiosInstance.post(endpoints.csc, {
          csc_id: 3,
          view_type: "view",
        });
        const cscOptions = Object.entries(cscRes.data.Data || {}).map(([value, label]) => ({
          value,
          label,
        }));

        setDropdownData((prev) => ({
          ...prev,
          est_id: estOptions,
          csc_id: cscOptions,
        }));
      } catch (error) {
        console.error("Error fetching static dropdowns:", error);
      }
    };
    fetchStaticDropdowns();

  }, []);

 
  const { options: statusOptions } = useDropdownFetch(endpoints.status);
  useEffect(() => {
    if (statusOptions) {
      setDropdownData((prev) => ({ ...prev, status_id: statusOptions }));
    }
  }, [statusOptions]);


  useEffect(() => {
    if (!formData.est_id) {
      setDropdownData((prev) => ({ ...prev, campusid: [] }));
      return;
    }
    const fetchCampuses = async () => {
      try {
        const res = await axiosInstance.post(endpoints.campus, {
          est_id: formData.est_id,
          view_type: "view",
        });
        const campusOptions = Object.entries(res.data.Data || {}).map(([value, label]) => ({
          value,
          label,
        }));
        setDropdownData((prev) => ({ ...prev, campusid: campusOptions }));
      } catch (error) {
        console.error("Error fetching campuses:", error);
      }
    };
    fetchCampuses();
  }, [formData.est_id]);

  useEffect(() => {
    if (!formData.csc_id) {
      setDropdownData((prev) => ({ ...prev, area_id: [] }));
      return;
    }
    const fetchAreas = async () => {
      try {
        const res = await axiosInstance.post(endpoints.area, {
          csc_id: formData.csc_id,
          view_type: "view",
        });
        const areaOptions = Object.entries(res.data.Data || {}).map(([value, label]) => ({
          value,
          label,
        }));
        setDropdownData((prev) => ({ ...prev, area_id: areaOptions }));
      } catch (error) {
        console.error("Error fetching areas:", error);
      }
    };
    fetchAreas();
  }, [formData.csc_id]);

  
  useEffect(() => {
    if (!selectedEstId) {
      setDropdownData((prev) => ({ ...prev, campusid: [] }));
      return;
    }
    const fetchCampusesForFilter = async () => {
      try {
        const res = await axiosInstance.post(endpoints.campus, {
          est_id: selectedEstId,
          view_type: "view",
        });
        const campusOptions = Object.entries(res.data.Data || {}).map(([value, label]) => ({
          value,
          label,
        }));
        setDropdownData((prev) => ({ ...prev, campusid: campusOptions }));
      } catch (error) {
        console.error("Error fetching campuses for modal filter:", error);
      }
    };
    fetchCampusesForFilter();
  }, [selectedEstId]);

  
  useEffect(() => {
    if (!selectedCampusId) {
      setEntities([]);
      setQrCodes({});
      return;
    }
    const fetchBuildings = async () => {
      try {
        const res = await axiosInstance.post(endpoints.buildings, {
          campusid: selectedCampusId,
          view_type: "view all",
        });
        const buildings = res.data.Data || [];
        setEntities(buildings);

        const qrData = {};
        buildings.forEach((b) => {
          if (b.bldg_qr_id) qrData[b.bldg_id] = b.bldg_qr_id;
        });
        setQrCodes(qrData);
      } catch (error) {
        console.error("Error fetching buildings for modal filter:", error);
      }
    };
    fetchBuildings();
  }, [selectedCampusId]);


  const handleViewTableClick = () => {
    if (!selectedEstId && formData.est_id) setSelectedEstId(formData.est_id);
    if (!selectedCampusId && formData.campusid) setSelectedCampusId(formData.campusid);
    setIsTableModalOpen(true);
  };


  const handleFormSubmit = async (e) => {
    e.preventDefault();

 
    const payload = {
      bldg_code: formData.bldg_code,
      bldg_no: formData.bldg_no,
      bldg_name: formData.bldg_name,
      est_id: parseInt(formData.est_id, 10),
      campusid: parseInt(formData.campusid, 10),
      area_id: parseInt(formData.area_id, 10),
      csc_id: parseInt(formData.csc_id, 10),
      status_id: parseInt(formData.status_id, 10),
    };

    try {
      if (editMode) {
        await handleUpdate(editingEntityId, payload);
      } else {
        const response = await axiosInstance.post(endpoints.registerBuilding, payload);
        if (response.data.Status === "Success") {
          // Refresh the building list in modal if filters match
          if (selectedCampusId === formData.campusid) {
            setEntities((prev) => [...prev, response.data.Data]);
          }
          resetForm();
        }
      }
    } catch (error) {
      console.error("Error adding building:", error);
    }
  };

  // --- UPDATE ---
  const handleUpdate = async (id, updatedData) => {
    try {
      const response = await axiosInstance.post(endpoints.updateBuilding, {
        ...updatedData,
        bldg_id: id,
      });
      if (response.data.Status === "Success") {
        setEntities((prev) =>
          prev.map((b) => (b.bldg_id === id ? { ...b, ...updatedData } : b))
        );
        resetForm();
        setIsTableModalOpen(false);
      }
    } catch (error) {
      console.error("Error updating building:", error);
    }
  };

  // --- EDIT ---
  const handleEdit = (id) => {
    const entity = entities.find((e) => e.bldg_id === id);
    if (entity) {
      setFormData({ ...entity });
      setEditMode(true);
      setEditingEntityId(id);
      if (firstFormFieldRef.current) firstFormFieldRef.current.focus();
      setIsTableModalOpen(false);
    }
  };

  // --- DELETE (Soft delete) ---
  const handleDelete = async (id) => {
    const entity = entities.find((e) => e.bldg_id === id);
    if (!entity) return;
    try {
      const response = await axiosInstance.post(endpoints.updateBuilding, {
        ...entity,
        bldg_id: id,
        status_id: 3,
      });
      if (response.data.Status === "Success") {
        setEntities((prev) =>
          prev.map((e) => (e.bldg_id === id ? { ...e, status_id: 3 } : e))
        );
      }
    } catch (error) {
      console.error("Error deleting building:", error);
    }
  };

  // --- Reset form to initial state ---
  const resetForm = () => {
    setFormData({
      bldg_code: "",
      bldg_no: "",
      bldg_name: "",
      est_id: "",
      campusid: "",
      area_id: "",
      csc_id: "",
      status_id: "",
      added_date: "",
    });
    setEditMode(false);
    setEditingEntityId(null);
  };

  // --- Handle QR Code display (if used) ---
  const handleGenerateQRCode = (id) => {
    const entity = entities.find((e) => e.bldg_id === id);
    if (entity && entity.bldg_qr_id) {
      setQrCodes((prev) => ({ ...prev, [id]: entity.bldg_qr_id }));
    }
  };

  // --- Form fields ---
  const formFields = [
    { name: "bldg_code", label: "Building Code", type: "text", required: true },
    { name: "bldg_no", label: "Building Number", type: "text", required: true },
    { name: "bldg_name", label: "Building Name", type: "text", required: true },
    {
      name: "est_id",
      label: "Establishment",
      type: "select",
      dropdownKey: "est_id",
      required: true,
    },
    {
      name: "campusid",
      label: "Campus",
      type: "select",
      dropdownKey: "campusid",
      required: true,
    },
    {
      name: "csc_id",
      label: "City",
      type: "select",
      dropdownKey: "csc_id",
      required: true,
    },
    {
      name: "area_id",
      label: "Area",
      type: "select",
      dropdownKey: "area_id",
      required: true,
    },
    {
      name: "status_id",
      label: "Status",
      type: "select",
      dropdownKey: "status_id",
      required: true,
    },
  ];

  // --- Table columns ---
  const columns = [
    { name: "bldg_code", label: "Building Code" },
    { name: "bldg_no", label: "Building Number" },
    { name: "bldg_name", label: "Building Name" },
    { name: "est_name", label: "Establishment" },
    { name: "campus_name", label: "Campus" },
    { name: "csc_name", label: "City" },
    { name: "area_name", label: "Area" },
    { name: "status_name", label: "Status" },
    { name: "bldg_qr_id", label: "QR" },
  ];

  // --- Styles ---
  const dropdownStyle = {
    padding: "10px 14px",
    fontSize: "14px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    width: "200px",
    backgroundColor: "#fff",
    color: "#111827",
    appearance: "none",
    backgroundSize: "1rem",
    cursor: "pointer",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  };

  const labelStyle = {
    fontWeight: "600",
    marginBottom: "0.6rem",
    fontSize: "14px",
    color: "#374151",
  };

  // Safe entities (no nulls)
  const safeEntities = (entities || []).filter((item) => item && item.bldg_id != null);

  return (
    <div className="building">
      <EntityManagement
        entityName="Building"
        formFields={formFields}
        formData={formData}
        setFormData={setFormData}
        handleFormSubmit={handleFormSubmit}
        dropdownData={dropdownData}
        setDropdownData={setDropdownData}
        firstFormFieldRef={firstFormFieldRef}
        handleViewTableClick={handleViewTableClick}
        onEdit={handleEdit}
      />

      {isTableModalOpen && (
        <div className="modal">
          <div className="modal-content" style={{ maxWidth: 900, margin: "auto" }}>
            <button onClick={() => setIsTableModalOpen(false)}>Close</button>

            {/* Filters */}
            <div
              style={{
                display: "flex",
                gap: "2rem",
                marginBottom: "1.5rem",
                backgroundColor: "#f9fafb",
                padding: "1rem",
                borderRadius: "8px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                border: "1px solid #e5e7eb",
                justifyContent: "center",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={labelStyle}>Filter by Establishment:</label>
                <select
                  value={selectedEstId}
                  onChange={(e) => {
                    setSelectedEstId(e.target.value);
                    setSelectedCampusId("");
                    setEntities([]);
                  }}
                  style={dropdownStyle}
                >
                  <option value="">All</option>
                  {dropdownData.est_id.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={labelStyle}>Filter by Campus:</label>
                <select
                  value={selectedCampusId}
                  onChange={(e) => setSelectedCampusId(e.target.value)}
                  style={dropdownStyle}
                >
                  <option value="">All</option>
                  {dropdownData.campusid.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <EntityTable
              data={safeEntities}
              columns={columns}
              onEdit={handleEdit}
              onDelete={handleDelete}
              keyField="bldg_id"
              onGenerateQRCode={handleGenerateQRCode}
              qrCodes={qrCodes}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Building;
